import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  TextField,
  TextArea,
  IconButton,
  Select,
  Callout,
  Tabs,
  Badge,
} from '@radix-ui/themes';
import {
  BellIcon,
  ArrowLeftIcon,
  InfoCircledIcon,
  PlusIcon,
  MixerHorizontalIcon,
  DiscordLogoIcon,
  Link2Icon,
  TrashIcon,
  ChatBubbleIcon,
} from '@radix-ui/react-icons';
import { useCreateAlertMutation } from '../../graphql/mutations/CreateAlert.generated';
import { GetAlertsDocument } from '../../graphql/queries/GetAlerts.generated';
import {
  useGetSignalsQuery,
  GetSignalsQuery,
} from '../../graphql/queries/GetSignals.generated';
import { useCreateSignalMutation } from '../../graphql/mutations/CreateSignal.generated';
import { GetSignalsDocument } from '../../graphql/queries/GetSignals.generated';
import {
  SignalType,
  Fingerprint,
  TransactionFingerprintInput,
  TransactionFingerprint,
  TransactionField,
  Signal,
  AlertDestinationType,
  CreateAlertDestinationInput,
} from '../../types';
import { SignalEditor } from '@/components';
import DiscordBotSelect from '../../components/DiscordBotSelect';
import DiscordBotChannelSelect from '../../components/DiscordBotChannelSelect';
import WebhookSelect from '../../components/WebhookSelect';
import SlackInstallationSelect from '../../components/SlackInstallationSelect';
import SlackInstallationChannelSelect from '../../components/SlackInstallationChannelSelect';

type SignalEdge = GetSignalsQuery['signals']['edges'][0];
type SignalNode = SignalEdge['node'];

interface PendingDestination {
  id: string;
  type: 'discord' | 'webhook' | 'slack';
  discordBotId?: string;
  discordChannelId?: string;
  webhookId?: string;
  slackInstallationId?: string;
  slackChannelId?: string;
  label: string;
}

const AlertCreate: React.FC = () => {
  const navigate = useNavigate();
  const [createAlert, { loading: creatingAlert }] = useCreateAlertMutation();
  const [createSignal, { loading: creatingSignal }] = useCreateSignalMutation();
  const { data: signalsData, loading: signalsLoading } = useGetSignalsQuery({
    variables: { first: 100 },
  });

  // Alert fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Signal selection
  const [signalMode, setSignalMode] = useState<'existing' | 'new'>('existing');
  const [selectedSignalId, setSelectedSignalId] = useState('');

  // New signal fields
  const [newSignalName, setNewSignalName] = useState('');
  const [signalType, setSignalType] = useState<SignalType>(SignalType.Block);
  const [fingerprints, setFingerprints] = useState<Fingerprint[]>([]);

  // Destination fields
  const [pendingDestinations, setPendingDestinations] = useState<
    PendingDestination[]
  >([]);
  const [isAddingDestination, setIsAddingDestination] = useState(false);
  const [newDestinationType, setNewDestinationType] = useState<
    'discord' | 'webhook' | 'slack'
  >('discord');
  const [selectedBotId, setSelectedBotId] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [selectedWebhookId, setSelectedWebhookId] = useState('');
  const [selectedSlackInstallationId, setSelectedSlackInstallationId] = useState('');
  const [selectedSlackChannelId, setSelectedSlackChannelId] = useState('');

  const signals: SignalNode[] =
    signalsData?.signals.edges.map((edge: SignalEdge) => edge.node) ?? [];

  // Check if the signal type requires fingerprints
  const requiresFingerprints =
    signalType === SignalType.Transaction || signalType === SignalType.Log;

  // Validation
  const canCreateWithExisting =
    name.trim().length > 0 && selectedSignalId.length > 0 && pendingDestinations.length > 0;
  const canCreateWithNew =
    name.trim().length > 0 &&
    newSignalName.trim().length > 0 &&
    (!requiresFingerprints || fingerprints.length > 0) &&
    pendingDestinations.length > 0;
  const canCreate =
    signalMode === 'existing' ? canCreateWithExisting : canCreateWithNew;

  const canAddDestination =
    newDestinationType === 'discord'
      ? selectedBotId && selectedChannelId
      : newDestinationType === 'slack'
        ? selectedSlackInstallationId && selectedSlackChannelId
        : selectedWebhookId;

  // Create a mock signal object for the SignalEditor
  const mockSignal: Signal = {
    id: '',
    name: newSignalName,
    signalType,
    fingerprints,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const handleSignalChange = (updatedSignal: Signal) => {
    setFingerprints(updatedSignal.fingerprints);
  };

  const handleAddDestination = () => {
    if (!canAddDestination) return;

    let label = 'Destination';
    if (newDestinationType === 'discord') {
      label = `Discord Bot (Channel: ${selectedChannelId})`;
    } else if (newDestinationType === 'slack') {
      label = `Slack Bot (Channel: ${selectedSlackChannelId})`;
    } else {
      label = 'Webhook';
    }

    const newDestination: PendingDestination = {
      id: crypto.randomUUID(),
      type: newDestinationType,
      discordBotId:
        newDestinationType === 'discord' ? selectedBotId : undefined,
      discordChannelId:
        newDestinationType === 'discord' ? selectedChannelId : undefined,
      webhookId:
        newDestinationType === 'webhook' ? selectedWebhookId : undefined,
      slackInstallationId:
        newDestinationType === 'slack' ? selectedSlackInstallationId : undefined,
      slackChannelId:
        newDestinationType === 'slack' ? selectedSlackChannelId : undefined,
      label,
    };

    setPendingDestinations([...pendingDestinations, newDestination]);
    setIsAddingDestination(false);
    setSelectedBotId('');
    setSelectedChannelId('');
    setSelectedWebhookId('');
    setSelectedSlackInstallationId('');
    setSelectedSlackChannelId('');
  };

  const handleRemoveDestination = (id: string) => {
    setPendingDestinations(pendingDestinations.filter((d) => d.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Alert name is required');
      return;
    }

    try {
      let signalId = selectedSignalId;

      // If creating a new signal, create it first
      if (signalMode === 'new') {
        if (!newSignalName.trim()) {
          setError('Signal name is required');
          return;
        }

        if (requiresFingerprints && fingerprints.length === 0) {
          setError('At least one condition is required for this signal type');
          return;
        }

        // Convert fingerprints to the input format expected by the backend
        const fingerprintInputs: TransactionFingerprintInput[] =
          fingerprints.map((fp) => {
            const txFp = fp as TransactionFingerprint;
            return {
              field: txFp.txField as unknown as TransactionField,
              operator: txFp.operation,
              value: txFp.value?.value,
            };
          });

        const signalResult = await createSignal({
          variables: {
            input: {
              name: newSignalName.trim(),
              signalType,
              fingerprints:
                fingerprintInputs.length > 0 ? fingerprintInputs : undefined,
            },
          },
          refetchQueries: [
            { query: GetSignalsDocument, variables: { first: 100 } },
          ],
        });

        if (!signalResult.data?.createSignal?.id) {
          setError('Failed to create signal');
          return;
        }

        signalId = signalResult.data.createSignal.id;
      }

      if (!signalId) {
        setError('Please select or create a signal');
        return;
      }

      // Convert pending destinations to inline format
      const destinations: CreateAlertDestinationInput[] = pendingDestinations.map(
        (dest) => {
          if (dest.type === 'discord') {
            return {
              destinationType: AlertDestinationType.Discord,
              discordBotId: dest.discordBotId,
              discordChannelId: dest.discordChannelId,
            };
          } else if (dest.type === 'slack') {
            return {
              destinationType: AlertDestinationType.Slack,
              slackInstallationId: dest.slackInstallationId,
              slackChannelId: dest.slackChannelId,
            };
          } else {
            return {
              destinationType: AlertDestinationType.Webhook,
              webhookId: dest.webhookId,
            };
          }
        }
      );

      // Create the alert with inline destinations
      const result = await createAlert({
        variables: {
          input: {
            name: name.trim(),
            description: description.trim() || undefined,
            signalId,
            destinations: destinations.length > 0 ? destinations : undefined,
          },
        },
        refetchQueries: [
          { query: GetAlertsDocument, variables: { first: 50 } },
        ],
      });

      if (result.data?.createAlert) {
        navigate(`/alerts/${result.data.createAlert.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create alert');
    }
  };

  const loading = creatingAlert || creatingSignal;

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" gap="3">
          <IconButton
            variant="ghost"
            color="gray"
            onClick={() => navigate('/alerts')}
          >
            <ArrowLeftIcon width={20} height={20} />
          </IconButton>
          <Box
            style={{
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: 'var(--amber-3)',
              color: 'var(--amber-11)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BellIcon width={24} height={24} />
          </Box>
          <Box>
            <Heading as="h1" size="5" weight="bold">
              Create Alert
            </Heading>
            <Text size="2" color="gray">
              Get notified when specific conditions are met
            </Text>
          </Box>
        </Flex>
      </Box>

      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="4">
          {/* Alert Details */}
          <Card size="2">
            <Heading as="h3" size="3" weight="medium" mb="3">
              Alert Details
            </Heading>
            <Flex direction="column" gap="4">
              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  Name
                </Text>
                <TextField.Root
                  placeholder="Enter alert name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  Description
                </Text>
                <TextArea
                  placeholder="Optional description for this alert"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Box>
            </Flex>
          </Card>

          {/* Signal Selection */}
          <Card size="2">
            <Heading as="h3" size="3" weight="medium" mb="3">
              <Flex align="center" gap="2">
                <MixerHorizontalIcon width={16} height={16} />
                Signal
              </Flex>
            </Heading>

            <Tabs.Root
              value={signalMode}
              onValueChange={(v) => setSignalMode(v as 'existing' | 'new')}
            >
              <Tabs.List size="2" mb="4">
                <Tabs.Trigger value="existing">
                  Use Existing Signal
                </Tabs.Trigger>
                <Tabs.Trigger value="new">
                  <PlusIcon width={14} height={14} />
                  Create New Signal
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="existing">
                <Flex direction="column" gap="3">
                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1">
                      Select Signal
                    </Text>
                    <Select.Root
                      value={selectedSignalId}
                      onValueChange={setSelectedSignalId}
                    >
                      <Select.Trigger
                        placeholder={
                          signalsLoading
                            ? 'Loading signals...'
                            : 'Select a signal'
                        }
                        style={{ width: '100%' }}
                      />
                      <Select.Content>
                        {signals.length === 0 ? (
                          <Select.Item value="__none__" disabled>
                            No signals available
                          </Select.Item>
                        ) : (
                          signals.map((signal) => (
                            <Select.Item key={signal.id} value={signal.id}>
                              <Flex align="center" gap="2">
                                {signal.name}
                                <Badge size="1" variant="soft" color="gray">
                                  {signal.signalType}
                                </Badge>
                              </Flex>
                            </Select.Item>
                          ))
                        )}
                      </Select.Content>
                    </Select.Root>
                    <Text size="1" color="gray" mt="1">
                      The signal that triggers this alert
                    </Text>
                  </Box>

                  {signals.length === 0 && !signalsLoading && (
                    <Callout.Root color="blue" size="1">
                      <Callout.Icon>
                        <InfoCircledIcon />
                      </Callout.Icon>
                      <Callout.Text>
                        No signals found. Switch to &quot;Create New
                        Signal&quot; to create one.
                      </Callout.Text>
                    </Callout.Root>
                  )}
                </Flex>
              </Tabs.Content>

              <Tabs.Content value="new">
                <Flex direction="column" gap="4">
                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1">
                      Signal Name
                    </Text>
                    <TextField.Root
                      placeholder="Enter signal name"
                      value={newSignalName}
                      onChange={(e) => setNewSignalName(e.target.value)}
                    />
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1">
                      Signal Type
                    </Text>
                    <Select.Root
                      value={signalType}
                      onValueChange={(value) => {
                        setSignalType(value as SignalType);
                        // Clear fingerprints when changing signal type
                        setFingerprints([]);
                      }}
                    >
                      <Select.Trigger style={{ width: '100%' }} />
                      <Select.Content>
                        <Select.Item value={SignalType.Block}>
                          Block
                        </Select.Item>
                        <Select.Item value={SignalType.Network}>
                          Network
                        </Select.Item>
                        <Select.Item value={SignalType.Transaction}>
                          Transaction
                        </Select.Item>
                        <Select.Item value={SignalType.Log}>Log</Select.Item>
                      </Select.Content>
                    </Select.Root>
                    <Text size="1" color="gray" mt="1">
                      The type of blockchain data this signal monitors
                    </Text>
                  </Box>

                  {requiresFingerprints && (
                    <Callout.Root color="blue" size="1">
                      <Callout.Icon>
                        <InfoCircledIcon />
                      </Callout.Icon>
                      <Callout.Text>
                        {signalType === SignalType.Transaction
                          ? 'Transaction signals require at least one condition to filter transactions.'
                          : 'Log signals require at least one condition to filter event logs.'}
                      </Callout.Text>
                    </Callout.Root>
                  )}

                  <SignalEditor
                    signal={mockSignal}
                    onChange={handleSignalChange}
                    showHeader={true}
                  />
                </Flex>
              </Tabs.Content>
            </Tabs.Root>
          </Card>

          {/* Destinations */}
          <Card size="2">
            <Flex justify="between" align="center" mb="3">
              <Heading as="h3" size="3" weight="medium">
                <Flex align="center" gap="2">
                  <BellIcon width={16} height={16} />
                  Destinations
                </Flex>
              </Heading>
              {!isAddingDestination && (
                <Button
                  type="button"
                  variant="soft"
                  size="1"
                  onClick={() => setIsAddingDestination(true)}
                >
                  <PlusIcon />
                  Add Destination
                </Button>
              )}
            </Flex>

            {/* List of pending destinations */}
            {pendingDestinations.length > 0 && (
              <Flex direction="column" gap="2" mb="3">
                {pendingDestinations.map((dest) => (
                  <Flex
                    key={dest.id}
                    align="center"
                    justify="between"
                    p="2"
                    style={{
                      backgroundColor: 'var(--gray-3)',
                      borderRadius: 'var(--radius-2)',
                    }}
                  >
                    <Flex align="center" gap="2">
                      {dest.type === 'discord' ? (
                        <DiscordLogoIcon />
                      ) : dest.type === 'slack' ? (
                        <ChatBubbleIcon />
                      ) : (
                        <Link2Icon />
                      )}
                      <Text size="2">{dest.label}</Text>
                    </Flex>
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="red"
                      size="1"
                      onClick={() => handleRemoveDestination(dest.id)}
                    >
                      <TrashIcon />
                    </IconButton>
                  </Flex>
                ))}
              </Flex>
            )}

            {/* Add destination form */}
            {isAddingDestination && (
              <Card variant="surface" size="1">
                <Flex direction="column" gap="3">
                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1">
                      Destination Type
                    </Text>
                    <Select.Root
                      value={newDestinationType}
                      onValueChange={(value) => {
                        setNewDestinationType(
                          value as 'discord' | 'webhook' | 'slack'
                        );
                        setSelectedBotId('');
                        setSelectedChannelId('');
                        setSelectedWebhookId('');
                        setSelectedSlackInstallationId('');
                        setSelectedSlackChannelId('');
                      }}
                    >
                      <Select.Trigger style={{ width: '100%' }} />
                      <Select.Content>
                        <Select.Item value="discord">
                          <Flex align="center" gap="2">
                            <DiscordLogoIcon />
                            Discord Bot
                          </Flex>
                        </Select.Item>
                        <Select.Item value="slack">
                          <Flex align="center" gap="2">
                            <ChatBubbleIcon />
                            Slack
                          </Flex>
                        </Select.Item>
                        <Select.Item value="webhook">
                          <Flex align="center" gap="2">
                            <Link2Icon />
                            Webhook
                          </Flex>
                        </Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Box>

                  {newDestinationType === 'discord' ? (
                    <>
                      <Box>
                        <Text as="label" size="2" weight="medium" mb="1">
                          Discord Bot
                        </Text>
                        <DiscordBotSelect
                          botId={selectedBotId}
                          onValueChange={setSelectedBotId}
                        />
                      </Box>
                      <Box>
                        <Text as="label" size="2" weight="medium" mb="1">
                          Channel
                        </Text>
                        <DiscordBotChannelSelect
                          botId={selectedBotId}
                          channelId={selectedChannelId}
                          onValueChange={setSelectedChannelId}
                          disabled={!selectedBotId}
                        />
                      </Box>
                    </>
                  ) : newDestinationType === 'slack' ? (
                    <>
                      <Box>
                        <Text as="label" size="2" weight="medium" mb="1">
                          Slack Workspace
                        </Text>
                        <SlackInstallationSelect
                          installationId={selectedSlackInstallationId}
                          onValueChange={setSelectedSlackInstallationId}
                        />
                      </Box>
                      <Box>
                        <Text as="label" size="2" weight="medium" mb="1">
                          Channel
                        </Text>
                        <SlackInstallationChannelSelect
                          installationId={selectedSlackInstallationId}
                          channelId={selectedSlackChannelId}
                          onValueChange={setSelectedSlackChannelId}
                          disabled={!selectedSlackInstallationId}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Text as="label" size="2" weight="medium" mb="1">
                        Webhook
                      </Text>
                      <WebhookSelect
                        webhookId={selectedWebhookId}
                        onValueChange={setSelectedWebhookId}
                      />
                    </Box>
                  )}

                  <Flex gap="2" justify="end">
                    <Button
                      type="button"
                      variant="soft"
                      color="gray"
                      size="1"
                      onClick={() => {
                        setIsAddingDestination(false);
                        setSelectedBotId('');
                        setSelectedChannelId('');
                        setSelectedWebhookId('');
                        setSelectedSlackInstallationId('');
                        setSelectedSlackChannelId('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="1"
                      disabled={!canAddDestination}
                      onClick={handleAddDestination}
                    >
                      Add
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            )}

            {pendingDestinations.length === 0 && !isAddingDestination && (
              <Text size="2" color="amber">
                At least one destination is required. Click &quot;Add Destination&quot; to
                configure where notifications will be sent when this alert triggers.
              </Text>
            )}
          </Card>

          {error && (
            <Text color="red" size="2">
              {error}
            </Text>
          )}

          <Flex gap="3" justify="end" mt="2">
            <Button
              type="button"
              variant="soft"
              color="gray"
              onClick={() => navigate('/alerts')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !canCreate}>
              {loading ? 'Creating...' : 'Create Alert'}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Box>
  );
};

export default AlertCreate;
